import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/auth";
import { CRM_STAGES } from "@/data/demo";
import { PageHeader, Btn, Icon, Card } from "@/components/kit";
import { toast } from "sonner";

const STAGE_COLOR = { Lead: "#9CA3AF", Contacted: "#06B6D4", Meeting: "#3B82F6", Proposal: "#8B5CF6", Invested: "#F97316", "Active Client": "#84CC16" };

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", source: "Referral", location: "", score: 60, stage: "Lead", note: "" });

  const load = useCallback(() => api.get("/crm/leads").then((r) => setLeads(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const move = async (lead, dir) => {
    const idx = CRM_STAGES.indexOf(lead.stage);
    const ni = Math.min(Math.max(idx + dir, 0), CRM_STAGES.length - 1);
    const stage = CRM_STAGES[ni];
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, stage } : l)));
    await api.patch(`/crm/leads/${lead.id}`, { stage });
  };
  const remove = async (id) => { setLeads((ls) => ls.filter((l) => l.id !== id)); await api.delete(`/crm/leads/${id}`); };
  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const { data } = await api.post("/crm/leads", { ...form, score: Number(form.score) });
    setLeads((ls) => [data, ...ls]); setAdding(false); setForm({ name: "", source: "Referral", location: "", score: 60, stage: "Lead", note: "" });
    toast.success("Lead added");
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Clients & CRM" subtitle="Lead → Contacted → Meeting → Proposal → Invested → Active Client" icon="Users" color="#8B5CF6"
        action={<Btn onClick={() => setAdding(true)} data-testid="crm-add-lead-btn"><Icon name="Plus" size={16} /> Add Lead</Btn>} />

      {/* Next best action */}
      <div className="rounded-2xl grad-blue-violet text-white p-5 mb-6 flex items-center gap-4">
        <Icon name="Sparkles" size={22} />
        <div><div className="text-xs uppercase tracking-widest font-bold opacity-80">Next Best Action</div>
          <p className="font-medium">Call Rajesh Sharma — his SIP stopped 45 days ago. High re-activation potential.</p></div>
        <Btn variant="dark" className="ml-auto bg-white/15 hidden sm:inline-flex">Draft WhatsApp</Btn>
      </div>

      {adding && (
        <Card className="mb-6">
          <div className="grid sm:grid-cols-3 gap-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="crm-name-input" className={inp} />
            <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inp} />
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inp}>{["Referral", "LinkedIn", "Instagram", "Google", "Website", "Event"].map((s) => <option key={s}>{s}</option>)}</select>
            <input placeholder="Lead score" type="number" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className={inp} />
            <input placeholder="Next action / note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inp} sm:col-span-2`} />
          </div>
          <div className="flex gap-2 mt-3"><Btn onClick={add} data-testid="crm-save-lead-btn">Save Lead</Btn><Btn variant="outline" onClick={() => setAdding(false)}>Cancel</Btn></div>
        </Card>
      )}

      {/* Kanban */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {CRM_STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage);
          return (
            <div key={stage} className="min-w-0" data-testid={`crm-col-${stage.replace(/\s/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLOR[stage] }} />
                <span className="text-sm font-semibold">{stage}</span>
                <span className="ml-auto text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((l) => (
                  <div key={l.id} className="card-mfx p-4 hover-lift" data-testid="crm-lead-card">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-sm">{l.name}</div>
                      <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: `${l.score >= 80 ? "#84CC16" : l.score >= 65 ? "#F59E0B" : "#9CA3AF"}22`, color: l.score >= 80 ? "#65A30D" : l.score >= 65 ? "#D97706" : "#6B7280" }}>{l.score}</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-2"><Icon name="MapPin" size={11} />{l.location || "—"} · {l.source}</div>
                    {(l.aum || l.sip) && <div className="flex flex-wrap gap-1.5 mt-2">
                      {l.aum && <span className="text-[10px] font-semibold bg-blue-50 text-blue-600 rounded-full px-2 py-0.5">AUM {l.aum}</span>}
                      {l.sip && <span className="text-[10px] font-semibold bg-violet-50 text-violet-600 rounded-full px-2 py-0.5">SIP {l.sip}</span>}
                    </div>}
                    {l.note && <p className="text-xs text-gray-500 mt-2 line-clamp-2">{l.note}</p>}
                    <div className="flex items-center gap-1 mt-3">
                      <button onClick={() => move(l, -1)} className="text-gray-400 hover:text-gray-700 p-1" data-testid="crm-move-back"><Icon name="ChevronLeft" size={15} /></button>
                      <button onClick={() => move(l, 1)} className="text-gray-400 hover:text-blue-600 p-1" data-testid="crm-move-fwd"><Icon name="ChevronRight" size={15} /></button>
                      <button onClick={() => remove(l.id)} className="ml-auto text-gray-300 hover:text-red-500 p-1"><Icon name="Trash2" size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
const inp = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400";
